"use strict";
import { CurrencyAmount } from "@uniswap/sdk-core";
import { useAccount } from "hooks/useAccount";
import { useTokenBalances } from "hooks/useTokenBalances";
import JSBI from "jsbi";
import { useMemo } from "react";
import { ValueType, getCurrencyAmount } from "uniswap/src/features/tokens/getCurrencyAmount";
import { isAddress } from "utilities/src/addresses";
import { currencyKey } from "utils/currencyKey";
import { assume0xAddress } from "utils/wagmi";
import { erc20Abi } from "viem";
import { useBalance, useReadContracts } from "wagmi";
export function useRpcTokenBalancesWithLoadingIndicator({
  address,
  tokens,
  skip
}) {
  const { chainId } = useAccount();
  const validatedTokens = useMemo(
    () => skip ? [] : tokens?.filter((t) => isAddress(t?.address) !== false && t?.chainId === chainId) ?? [],
    [chainId, tokens, skip]
  );
  const { data, isLoading: balancesLoading } = useReadContracts({
    contracts: useMemo(
      () => validatedTokens.map(
        (token) => ({
          address: assume0xAddress(token.address),
          chainId,
          abi: erc20Abi,
          functionName: "balanceOf",
          args: [address]
        })
      ),
      [address, chainId, validatedTokens]
    ),
    query: { enabled: !!address }
  });
  return useMemo(
    () => [
      address && validatedTokens.length > 0 ? (
        // eslint-disable-next-line max-params
        validatedTokens.reduce((memo, token, i) => {
          const value = data?.[i].result;
          if (!value) {
            return memo;
          }
          const amount = value ? JSBI.BigInt(value.toString()) : void 0;
          if (amount) {
            memo[token.address] = CurrencyAmount.fromRawAmount(token, amount);
          }
          return memo;
        }, {})
      ) : {},
      balancesLoading
    ],
    [address, validatedTokens, balancesLoading, data]
  );
}
function useRpcTokenBalances(address, tokens) {
  return useRpcTokenBalancesWithLoadingIndicator({ address, tokens })[0];
}
function useRpcCurrencyBalances(account, currencies) {
  const tokens = useMemo(
    () => currencies?.filter((currency) => currency?.isToken ?? false) ?? [],
    [currencies]
  );
  const { chainId } = useAccount();
  const tokenBalances = useRpcTokenBalances(account, tokens);
  const containsETH = useMemo(() => currencies?.some((currency) => currency?.isNative) ?? false, [currencies]);
  const { data: nativeBalance } = useBalance({
    address: assume0xAddress(account),
    chainId,
    query: { enabled: containsETH && !!account }
  });
  return useMemo(
    () => currencies?.map((currency) => {
      if (!account || !currency || currency.chainId !== chainId) {
        return void 0;
      }
      if (currency.isToken) {
        return tokenBalances[currency.address];
      } else if (nativeBalance?.value) {
        return CurrencyAmount.fromRawAmount(currency, nativeBalance.value.toString());
      } else {
        return void 0;
      }
    }) ?? [],
    [account, chainId, currencies, nativeBalance?.value, tokenBalances]
  );
}
function useGqlCurrencyBalances(account, currencies) {
  const { balanceMap } = useTokenBalances({ cacheOnly: true });
  return useMemo(() => {
    if (!account || !currencies) {
      return [];
    }
    return currencies.map((currency) => {
      if (!currency) {
        return void 0;
      }
      const key = currencyKey(currency);
      const balance = balanceMap[key];
      if (balance) {
        const currencyAmount = getCurrencyAmount({
          value: balance.balance.toString(),
          valueType: ValueType.Exact,
          currency
        });
        if (!currencyAmount) {
          return void 0;
        }
        return currencyAmount;
      } else {
        return CurrencyAmount.fromRawAmount(currency, 0);
      }
    });
  }, [account, balanceMap, currencies]);
}
export function useCurrencyBalances(account, currencies) {
  const { chainId: providerChainId } = useAccount();
  const chainId = useMemo(() => currencies?.[0]?.chainId, [currencies]);
  const isSynced = !chainId || chainId === providerChainId;
  const gqlCurrencyBalances = useGqlCurrencyBalances(account, currencies);
  const rpcCurrencyBalances = useRpcCurrencyBalances(account, currencies);
  return useMemo(() => {
    if (!account || !currencies) {
      return [];
    }
    return isSynced ? rpcCurrencyBalances : gqlCurrencyBalances;
  }, [account, currencies, isSynced, gqlCurrencyBalances, rpcCurrencyBalances]);
}
export function useTokenBalance(account, token) {
  return useCurrencyBalance(account, token);
}
export default function useCurrencyBalance(account, currency) {
  return useCurrencyBalances(
    account,
    useMemo(() => [currency], [currency])
  )[0];
}
